import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { bookingApi, paymentApi, roomApi } from '../services/api';
import { parseRRuleToHumanReadable } from '../utils/recurrenceParser';
import Layout from './layout/Layout';
import './Dashboard.css';

// M007-F010 / #59: Group recurring instances into a single entry
// Shows one card per recurring series with the parsed RRULE label.
//
// Core's Google import writes google_recurring_event_id = NULL for a
// MASTER recurring row (it IS the master — google-calendar.service.ts's
// own convention, see the INSERT/UPDATE around lines 2421/2531). Such a
// row is a single entry in `bookings`, not a group of instances, but it
// still carries a real google_recurrence_rule (+ series_end_time) and
// must render the same "Weekly on <Day>, until <Month Day, Year>" label
// instance-based series get — instead of silently falling into the
// "not recurring" branch and showing nothing, which is defect #59.
// Exported (module scope, pure function of its `bookings` argument) so
// unit tests can exercise it directly without rendering the component.
export const groupRecurringBookings = (bookings) => {
  const grouped = [];
  const seenSeriesIds = new Set();

  for (const booking of bookings) {
    // Check if this is a recurring instance
    if (booking.google_recurring_event_id) {
      // Only show the first instance of each recurring series
      if (seenSeriesIds.has(booking.google_recurring_event_id)) {
        continue; // Skip - already have this series
      }
      seenSeriesIds.add(booking.google_recurring_event_id);

      // Count how many instances in this series
      const seriesInstances = bookings.filter(
        b => b.google_recurring_event_id === booking.google_recurring_event_id
      );

      // Add metadata for display
      const recurrenceLabel = booking.google_recurrence_rule
        ? parseRRuleToHumanReadable(booking.google_recurrence_rule)
        : 'Recurring series';
      grouped.push({
        ...booking,
        _isRecurringSeries: true,
        _recurrenceCount: seriesInstances.length,
        _recurrenceLabel: recurrenceLabel,
      });
    } else if (booking.google_recurrence_rule) {
      // #59: a MASTER row (no google_recurring_event_id, but it carries
      // its own rule) is a series of one row in this array. Reuse the
      // same parser instance-based series use above — never a second
      // parser — so the label is truthful: it reflects whatever the rule
      // itself says (an "until" date when the rule has one, nothing
      // invented when it doesn't).
      grouped.push({
        ...booking,
        _isRecurringSeries: true,
        _recurrenceCount: 1,
        _recurrenceLabel: parseRRuleToHumanReadable(booking.google_recurrence_rule),
      });
    } else {
      // Not a recurring instance - show as-is
      grouped.push(booking);
    }
  }

  return grouped;
};

const Dashboard = () => {
  const { user } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [climateData, setClimateData] = useState(null);
  const [climateLoading, setClimateLoading] = useState(false);
  const [tempInput, setTempInput] = useState('');
  const [climateUpdating, setClimateUpdating] = useState(false);
  const [climateMessage, setClimateMessage] = useState({ type: '', text: '' });

  // Check if booking is within active window (20 mins before start to end time)
  const isBookingActive = (booking) => {
    if (!booking?.start_time) return false;
    const now = new Date();
    const startTime = new Date(booking.start_time);
    const endTime = booking.end_time ? new Date(booking.end_time) : new Date(startTime.getTime() + 60 * 60 * 1000); // default 1hr if no end
    const windowStart = new Date(startTime.getTime() - 20 * 60 * 1000); // 20 mins before
    return now >= windowStart && now <= endTime;
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const bookingsRes = await bookingApi.getMyBookings();
        // API returns { data: { success, data: [...] } } via axios
        const bookingsArray = bookingsRes.data?.data || bookingsRes.data || [];
        setBookings(Array.isArray(bookingsArray) ? bookingsArray : []);
      } catch (err) {
        // No bookings available or error fetching
        console.log('Could not fetch bookings:', err.message);
        setBookings([]);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Fetch climate data when next booking is active
  useEffect(() => {
    const bookingsArray = Array.isArray(bookings) ? bookings : [];
    const nextBooking = bookingsArray[0];
    
    // Debug logging
    console.log('Dashboard: nextBooking =', nextBooking);
    console.log('Dashboard: isBookingActive =', nextBooking ? isBookingActive(nextBooking) : 'N/A');
    console.log('Dashboard: room_id =', nextBooking?.room_id);
    
    if (nextBooking && isBookingActive(nextBooking) && nextBooking.room_id) {
      const fetchClimate = async () => {
        setClimateLoading(true);
        try {
          console.log('Dashboard: Fetching climate for room', nextBooking.room_id);
          const response = await roomApi.getClimate(nextBooking.room_id);
          console.log('Dashboard: Climate response =', response.data);
          if (response.data?.data) {
            setClimateData(response.data.data);
          }
        } catch (err) {
          console.log('Could not fetch climate data:', err.message);
          setClimateData({ configured: false });
        } finally {
          setClimateLoading(false);
        }
      };
      fetchClimate();
      
      // Refresh climate data every 30 seconds while booking is active
      const interval = setInterval(fetchClimate, 30000);
      return () => clearInterval(interval);
    } else {
      setClimateData(null);
    }
  }, [bookings]);

  // Update tempInput when climate data loads
  useEffect(() => {
    if (climateData?.climate?.attributes?.temperature) {
      setTempInput(climateData.climate.attributes.temperature);
    }
  }, [climateData]);

  // Handle temperature change
  const handleSetTemperature = async () => {
    const bookingsArray = Array.isArray(bookings) ? bookings : [];
    const nextBooking = bookingsArray[0];
    if (!nextBooking?.room_id) return;

    const temp = parseFloat(tempInput);
    if (isNaN(temp) || temp < 15 || temp > 25) {
      setClimateMessage({ type: 'error', text: 'Temperature must be between 15-25°C' });
      return;
    }

    setClimateUpdating(true);
    setClimateMessage({ type: '', text: '' });

    try {
      await roomApi.setTemperature(nextBooking.room_id, temp);
      setClimateMessage({ type: 'success', text: 'Temperature updated' });
      // Refresh climate data
      const response = await roomApi.getClimate(nextBooking.room_id);
      if (response.data?.data) {
        setClimateData(response.data.data);
      }
    } catch (err) {
      setClimateMessage({ type: 'error', text: err.response?.data?.message || 'Failed to set temperature' });
    } finally {
      setClimateUpdating(false);
      setTimeout(() => setClimateMessage({ type: '', text: '' }), 3000);
    }
  };

  // Handle occupancy change
  const handleSetOccupancy = async (state) => {
    const bookingsArray = Array.isArray(bookings) ? bookings : [];
    const nextBooking = bookingsArray[0];
    if (!nextBooking?.room_id) return;

    setClimateUpdating(true);
    setClimateMessage({ type: '', text: '' });

    try {
      await roomApi.setOccupancy(nextBooking.room_id, state);
      setClimateMessage({ type: 'success', text: `Room set to ${state === 'InUse' ? 'In Use' : 'Not in Use'}` });
      // Refresh climate data
      const response = await roomApi.getClimate(nextBooking.room_id);
      if (response.data?.data) {
        setClimateData(response.data.data);
      }
    } catch (err) {
      setClimateMessage({ type: 'error', text: err.response?.data?.message || 'Failed to set occupancy' });
    } finally {
      setClimateUpdating(false);
      setTimeout(() => setClimateMessage({ type: '', text: '' }), 3000);
    }
  };

  // Ensure bookings is always an array for safe operations
  const bookingsArray = Array.isArray(bookings) ? bookings : [];
  // Filter to only show approved bookings
  const approvedBookings = bookingsArray.filter(b => b.status === 'approved');
  
  // Apply grouping to approved bookings
  const collapsedBookings = groupRecurringBookings(approvedBookings);
  
  const nextClass = collapsedBookings[0];
  const upcomingBookings = collapsedBookings.slice(0, 5);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getTimeUntil = (dateString) => {
    const now = new Date();
    const classDate = new Date(dateString);
    const diff = classDate - now;
    
    if (diff < 0) return 'In progress';
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);
    
    if (days > 0) return `in ${days} day${days > 1 ? 's' : ''}`;
    if (hours > 0) return `in ${hours} hour${hours > 1 ? 's' : ''}`;
    return 'Starting soon';
  };

  if (loading) {
    return (
      <Layout>
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading your dashboard...</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="dashboard">
        <header className="dashboard-header">
          <h1>Welcome back, {user?.displayName || 'Student'}!</h1>
          <p className="subtitle">Here's what's happening with your classes.</p>
        </header>

        <div className="dashboard-grid">
          {/* Next Class Widget */}
          <div className="widget widget-featured">
            <div className="widget-header">
              <h2>📅 Next Booking</h2>
              {nextClass && <span className="badge">{getTimeUntil(nextClass.start_time)}</span>}
            </div>
            {nextClass ? (
              <div className="next-class-content">
                <h3 className="class-name">{nextClass.title}</h3>
                <div className="class-details">
                  <p><span className="icon">🕐</span> {formatDate(nextClass.start_time)}</p>
                  <p><span className="icon">📍</span> {nextClass.room_name}</p>
                  <p><span className="icon">🏢</span> {nextClass.organisation_name}</p>
                </div>
                
                {/* Climate & Sensors Widget - Show when booking is active */}
                {isBookingActive(nextClass) && (
                  <div className="climate-widget">
                    <div className="climate-header">
                      <span>🌡️ Climate & Sensors</span>
                      {(climateLoading || climateUpdating) && <span className="climate-loading">⟳</span>}
                    </div>
                    
                    {climateMessage.text && (
                      <div className={`climate-message ${climateMessage.type}`}>
                        {climateMessage.text}
                      </div>
                    )}
                    
                    {climateData?.configured ? (
                      <div className="climate-data">
                        <div className="climate-row">
                          <span className="dash-climate-label">🌡️ Current:</span>
                          <span className="dash-climate-value">{climateData.climate?.attributes?.current_temperature || '--'}°C</span>
                        </div>
                        {climateData.lighting && (
                          <div className="climate-row">
                            <span className="dash-climate-label">💡 Lighting:</span>
                            <span className="dash-climate-value">
                              {climateData.lighting?.name?.state || 'N/A'}
                              {climateData.lighting?.index?.state && ` (${climateData.lighting.index.state}%)`}
                            </span>
                          </div>
                        )}
                        
                        {/* Temperature Control */}
                        <div className="climate-control">
                          <label className="climate-control-label">
                            Set Temperature: {tempInput || '--'}°C
                          </label>
                          <input
                            type="range"
                            min="15"
                            max="25"
                            step="0.5"
                            value={tempInput || 20}
                            onChange={(e) => setTempInput(e.target.value)}
                            onMouseUp={handleSetTemperature}
                            onTouchEnd={handleSetTemperature}
                            disabled={climateUpdating}
                            className="climate-slider"
                          />
                          <div className="climate-slider-labels">
                            <span>15°C</span>
                            <span>20°C</span>
                            <span>25°C</span>
                          </div>
                        </div>
                        
                        {/* Occupancy Control */}
                        <div className="climate-occupancy">
                          <button
                            onClick={() => handleSetOccupancy('UnUsed')}
                            disabled={climateUpdating}
                            className={`occupancy-btn ${climateData.occupancy?.state === 'UnUsed' ? 'active-off' : ''}`}
                          >
                            Off
                          </button>
                          <button
                            onClick={() => handleSetOccupancy('InUse')}
                            disabled={climateUpdating}
                            className={`occupancy-btn ${climateData.occupancy?.state === 'InUse' ? 'active-on' : ''}`}
                          >
                            On
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="climate-not-configured">
                        <p>⚠️ Climate sensors not configured for this room</p>
                      </div>
                    )}
                  </div>
                )}
                
                <Link to={`/app/book-room`} className="btn btn-primary">
                  View Bookings
                </Link>
              </div>
            ) : (
              <div className="empty-state">
                <p>No upcoming bookings scheduled.</p>
                <Link to="/app/book-room" className="btn btn-primary">Book a Room</Link>
              </div>
            )}
          </div>

          {/* Upcoming Bookings Widget */}
          <div className="widget">
            <div className="widget-header">
              <h2>📖 Upcoming Bookings</h2>
              <Link to="/app/book-room" className="link-all">View all</Link>
            </div>
            {upcomingBookings.length > 0 ? (
              <ul className="bookings-list">
                {upcomingBookings.map((booking) => (
                  <li key={booking.id} className="booking-item">
                    <div className="booking-info">
                      <span className="booking-name">
                        {booking.title}
                        {/* M007-F010: Show recurrence label for grouped series */}
                        {booking._isRecurringSeries && (
                          <span className="recurring-label" title={`${booking._recurrenceCount} instances`}>
                            🔁 {booking._recurrenceLabel}
                          </span>
                        )}
                      </span>
                      <span className="booking-time">{formatDate(booking.start_time)}</span>
                    </div>
                    <span className="booking-room">{booking.room_name}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="empty-state">
                <p>No bookings yet.</p>
                <Link to="/schedule" className="btn btn-outline">Book Now</Link>
              </div>
            )}
          </div>

          {/* Payment Status Widget */}
          <div className="widget">
            <div className="widget-header">
              <h2>💳 Payment Status</h2>
              <Link to="/app/payments" className="link-all">View all</Link>
            </div>
            <div className="payment-status">
              <div className="status-indicator success">
                <span className="status-icon">✓</span>
                <span className="status-text">All payments up to date</span>
              </div>
            </div>
          </div>

          {/* Quick Actions Widget */}
          <div className="widget">
            <div className="widget-header">
              <h2>⚡ Quick Actions</h2>
            </div>
            <div className="quick-actions">
              <Link to="/schedule" className="action-btn">
                <span className="action-icon">📅</span>
                <span>Book a Class</span>
              </Link>
              {(user?.role === 'teacher' || user?.role === 'admin' || user?.role === 'super_admin') && (
                <Link to="/app/book-room" className="action-btn">
                  <span className="action-icon">🚪</span>
                  <span>Book a Room</span>
                </Link>
              )}
              <Link to="/app/profile" className="action-btn">
                <span className="action-icon">👤</span>
                <span>Edit Profile</span>
              </Link>
              <Link to="/app/payments" className="action-btn">
                <span className="action-icon">💰</span>
                <span>View Invoices</span>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Dashboard;